import { closeIcon } from "@/assets/icons";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@customElement("sb-multi-email-input")
export class MultiEmailInput extends LitElement {
  @property({ type: String }) label = "";

  /** Array of valid email strings currently in the input. */
  @property({ type: Array }) value: string[] = [];

  /** Called whenever the emails array changes. */
  @property({ attribute: false }) onChange?: (emails: string[]) => void;

  /** Placeholder text when no emails are present. */
  @property({ type: String }) placeholder = "Enter email addresses";

  /** Disable the entire input. */
  @property({ type: Boolean }) disabled = false;

  /** Maximum number of emails allowed. */
  @property({ type: Number }) maxEmails = 50;

  /** Additional CSS class(es) to apply to the outer wrapper. */
  @property({ type: String }) className = "";

  @state() private inputValue = "";
  @state() private error: string | null = null;

  protected createRenderRoot() {
    return this; // light DOM
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */

  private validateEmail(email: string): boolean {
    return emailRegex.test(email.trim());
  }

  private addEmails(emailsToAdd: string[]) {
    const valid: string[] = [];
    const invalid: string[] = [];

    for (const raw of emailsToAdd) {
      const email = raw.trim();
      if (!email) continue;

      if (this.validateEmail(email)) {
        if (!this.value.includes(email) && !valid.includes(email)) {
          valid.push(email);
        }
      } else {
        invalid.push(email);
      }
    }

    if (valid.length > 0) {
      const newEmails = [...this.value, ...valid].slice(0, this.maxEmails);
      this.value = newEmails;
      this.inputValue = "";
      this.error = null;

      if (newEmails.length >= this.maxEmails) {
        this.error = `Maximum ${this.maxEmails} emails allowed`;
      }

      this.notifyChange(newEmails);
    }

    if (invalid.length > 0) {
      this.error = `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`;
    }
  }

  private notifyChange(emails: string[]) {
    this.onChange?.(emails);
    this.dispatchEvent(
      new CustomEvent("emails-changed", {
        detail: { emails },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Event handlers                                                    */
  /* ------------------------------------------------------------------ */

  private handleKeyDown(e: KeyboardEvent) {
    // Clear error while typing
    if (this.error && this.inputValue !== "") {
      this.error = null;
    }

    // Add on Enter, comma, semicolon, or space
    if (["Enter", ",", ";", " "].includes(e.key)) {
      e.preventDefault();
      if (this.inputValue.trim()) {
        this.addEmails([this.inputValue]);
      }
      return;
    }

    // Remove last email on Backspace when input is empty
    if (e.key === "Backspace" && !this.inputValue && this.value.length > 0) {
      const newEmails = this.value.slice(0, -1);
      this.value = newEmails;
      this.error = null;
      this.notifyChange(newEmails);
    }
  }

  private handlePaste(e: ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData?.getData("text") ?? "";
    const emails = text
      .split(/[;,\n\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    this.addEmails(emails);
  }

  private handleBlur() {
    if (this.inputValue.trim()) {
      this.addEmails([this.inputValue]);
    }
  }

  private removeEmail(emailToRemove: string) {
    const newEmails = this.value.filter((e) => e !== emailToRemove);
    this.value = newEmails;
    this.error = null;
    this.notifyChange(newEmails);
  }

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */

  render() {
    const isDisabled = this.disabled || this.value.length >= this.maxEmails;

    return html`
      <div class="sb-space-y-2 ${this.className}">
        ${this.label
          ? html`<rtg-label part="label">${this.label}</rtg-label>`
          : nothing}

        <div
          part="input-wrapper"
          class="${isDisabled ? "sb-cursor-not-allowed sb-opacity-50" : ""}"
        >
          ${this.value.map(
            (email) => html`
              <rtg-badge variant="secondary">
                <rtg-typography-small class="sb-truncate"
                  >${email}</rtg-typography-small
                >
                ${!this.disabled
                  ? html`
                      <span
                        class="sb-cursor-pointer sb-ml-1"
                        @click=${() => this.removeEmail(email)}
                      >
                        ${closeIcon()}
                      </span>
                    `
                  : nothing}
              </rtg-badge>
            `,
          )}

          <!-- Native input instead of sb-input -->
          <input
            type="text"
            .value=${this.inputValue}
            placeholder=${this.value.length === 0 ? this.placeholder : ""}
            ?disabled=${isDisabled}
            @input=${(e: Event) => {
              this.inputValue = (e.target as HTMLInputElement).value;
            }}
            @keydown=${this.handleKeyDown}
            @paste=${this.handlePaste}
            @blur=${this.handleBlur}
            class="disabled:sb-cursor-not-allowed"
          />
        </div>

        ${this.error
          ? html`<rtg-typography-small part="error">
              ${this.error}
            </rtg-typography-small>`
          : nothing}
        ${this.value.length > 0
          ? html`
              <rtg-typography-muted part="info">
                ${this.value.length} email${this.value.length !== 1 ? "s" : ""}
                added ${this.maxEmails ? `(max ${this.maxEmails})` : ""}
              </rtg-typography-muted>
            `
          : html`<rtg-typography-muted part="info">
              Up to ${this.maxEmails} email addresses
            </rtg-typography-muted>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-multi-email-input": MultiEmailInput;
  }
}
